<template>
  <div class="workout-session">
    <div class="container workout-session__header">
      <div class="workout-session__close-btn" @click="confirmExit">
        <v-icon icon="close" />
      </div>

      <alert-dialog
        title="Leave workout?"
        :actions="[
          {
            title: 'No, continue',
            callback: resume
          },
          {
            title: 'Finish Workout',
            callback: complete
          }
        ]"
        :show="showDialog"
        @close="resume"
      >
        Are you sure you want to exit this workout in progress?
      </alert-dialog>
    </div>

    <transition name="fade" mode="out-in">
      <template v-if="isReady">
        <div key="session-ready" class="workout-session__wrapper">
          <template v-if="isGroupedSession">
            <grouped-session
              :workout-id="workoutId"
              :groups="workoutGroups"
              :session="session"
              :workout-timer="workoutTimer"
              :workout-type="workoutType"
              :hide-timer="hideTimer"
              @complete="completeWorkout"
            />
          </template>
          <template v-else>
            <follow-along-session
              :workout-id="workoutId"
              :groups="workoutGroups"
              :session="session"
              :workout-timer="workoutTimer"
              :hide-timer="hideTimer"
              @complete="completeWorkout"
            />
          </template>

          <sign-up-prompt
            v-model="showSignUpPrompt"
            @dismiss="() => $emit('complete')"
          />
        </div>
      </template>

      <template v-else>
        <div
          key="session-loading"
          class="d-flex justify-content-center align-items-center mt-5 pt-5"
        >
          <v-loader />
        </div>
      </template>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"

import { WORKOUT_SESSION_MODES, WORKOUT_TYPES } from "@/store/workouts"

import enumValidator from "@/lib/validators/enum-validator"
import EventBus, { EVENTS } from "@/lib/event-bus"

import GroupedSession from "@/components/workout/GroupedSession"
import FollowAlongSession from "@/components/workout/FollowAlongSession"
import SignUpPrompt from "@/components/workout/SignUpPrompt"
import AlertDialog from "@/components/AlertDialog"

export default {
  props: {
    workoutId: { type: [Number, String], required: true },
    pageId: { type: Number, required: true },
    workoutGroups: { type: Array, required: true },
    workoutTimer: { type: Object, required: true },
    session: { type: Object, required: true },
    sessionMode: {
      type: String,
      default: WORKOUT_SESSION_MODES.FOLLOW_ALONG,
      validator: enumValidator(Object.values(WORKOUT_SESSION_MODES))
    },
    workoutType: {
      type: String,
      default: null,
      validator: enumValidator([null, WORKOUT_TYPES.BEEP_TEST])
    },
    audioSpriteMap: { type: Object, default: null },
    hideTimer: { type: Boolean, default: false }
  },

  components: {
    SignUpPrompt,
    GroupedSession,
    FollowAlongSession,

    AlertDialog
  },

  data() {
    return {
      activeGroupIndex: 0,
      groupTimer: null,
      sessionComplete: false,
      showSignUpPrompt: false,
      isReady: false,
      showDialog: false
    }
  },

  mounted() {
    setTimeout(() => (this.isReady = true), 480) // Min loading duration
    this.hidePageToolbar({ pageId: this.pageId, isHidden: true })
  },

  methods: {
    ...mapActions("workouts", ["completeSession", "recordSessionActivity"]),
    ...mapMutations("pages", ["hidePageToolbar"]),

    completeWorkout() {
      this.workoutTimer.pause()

      if (this.isAuthenticated) {
        this.$emit("complete")
      } else {
        this.showSignUpPrompt = true
      }
    },

    resume() {
      this.showDialog = false
      this.$emit("resume")
      EventBus.$emit(EVENTS.WORKOUT_RESUMED)
    },

    complete() {
      this.showDialog = false
      this.completeWorkout()
    },

    confirmExit() {
      EventBus.$emit(EVENTS.WORKOUT_EXIT_ATTEMPTED)
      this.showDialog = true
    }
  },

  beforeDestroy() {
    this.hidePageToolbar({ pageId: this.pageId, isHidden: false })
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),

    isGroupedSession() {
      return (
        this.sessionMode === WORKOUT_SESSION_MODES.GROUPED ||
        this.workoutType === WORKOUT_TYPES.BEEP_TEST
      )
    },

    isFollowAlongSession() {
      return this.sessionMode === WORKOUT_SESSION_MODES.FOLLOW_ALONG
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.workout-session__header {
  position: relative;
  z-index: 2;
}

.workout-session__close-btn {
  @include clickable(0.9);

  font-size: 1.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;

  background: $color-ui--white;
  position: relative;
  box-shadow: $box-shadow--medium;
  color: $color-ui--grey-70;
  border-radius: 50%;
  width: $size--9;
  height: $size--9;
  padding: 0 $size--4;
  margin-left: $size--2;
}
</style>
